import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Star, ArrowRight,
  Footprints, Shirt, Backpack, Smartphone, Droplet, CloudRain,
  Heart, Sparkles, Package,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FloatingActions from '../components/FloatingActions';
import SEOHead from '../components/SEOHead';
import axios from 'axios';

const BACKEND = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const CATEGORIES = ['All', 'Footwear', 'Apparel', 'Backpacks', 'Bags', 'Tech', 'Hydration', 'Recovery', 'Extras'];

// Category → icon for image placeholders. Falls back to Package.
const CATEGORY_ICON = {
  Footwear: Footprints,
  Apparel: Shirt,
  Backpacks: Backpack,
  Bags: Package,
  Tech: Smartphone,
  Hydration: Droplet,
  Weather: CloudRain,
  Recovery: Heart,
  Extras: Sparkles,
};

const GearListPage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');

  useEffect(() => {
    fetchGear();
  }, [activeCategory]);

  const fetchGear = async () => {
    setLoading(true);
    try {
      const params = activeCategory !== 'All' ? { category: activeCategory } : {};
      const res = await axios.get(`${BACKEND}/api/gear`, { params });
      setItems(res.data?.items || []);
    } catch (e) {
      console.error('Failed to fetch gear:', e);
    } finally {
      setLoading(false);
    }
  };

  const getPriceTierColor = (tier) => {
    switch (tier) {
      case 'Budget': return 'bg-green-100 text-green-800 hover:bg-green-100';
      case 'Mid': return 'bg-blue-100 text-blue-800 hover:bg-blue-100';
      case 'Premium': return 'bg-amber-100 text-amber-800 hover:bg-amber-100';
      default: return 'bg-gray-100 text-gray-700 hover:bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Walk-Ready Gear, Curated by Ben"
        description="Tested, trusted, and stylish gear for urban walking — curated by Ben."
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-slate-900 via-blue-900 to-purple-900 text-white py-16 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3">Walk-Ready Gear, Curated by Ben.</h1>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Tested, trusted, and walk-ready — every pick earned its place on the walk.
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <div className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1 overflow-x-auto py-3 -mx-4 px-4">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                  activeCategory === cat
                    ? 'bg-amber-500 text-white border-amber-500'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Gear Grid */}
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-gray-500">
            {loading ? 'Loading...' : `${items.length} item${items.length !== 1 ? 's' : ''} found`}
          </p>
          <p className="text-xs text-gray-400">As an Amazon Associate, Wanderloop earns from qualifying purchases.</p>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-lg border p-6 animate-pulse">
                <div className="h-48 bg-gray-200 rounded mb-4" />
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(item => (
              <Link key={item.slug} to={`/gear/${item.slug}`}>
                <Card className="h-full hover:shadow-lg transition-shadow group cursor-pointer bg-gradient-to-br from-amber-50 to-orange-50">
                  <CardContent className="p-0">
                    {/* Product Image */}
                    <div className="aspect-[4/3] bg-white rounded-t-lg flex items-center justify-center relative overflow-hidden p-4">
                      {item.images?.[0] ? (
                        <img src={item.images[0]} alt={item.name} className="w-full h-full object-contain" />
                      ) : (() => {
                        const Icon = CATEGORY_ICON[item.category] || Package;
                        return (
                          <div className="w-full h-full bg-gray-100 rounded flex flex-col items-center justify-center text-gray-400">
                            <Icon className="w-12 h-12 mb-1.5" strokeWidth={1.5} />
                            <p className="text-xs uppercase tracking-wide">{item.category}</p>
                          </div>
                        );
                      })()}
                      {item.price_tier && (
                        <Badge className={`absolute top-3 right-3 ${getPriceTierColor(item.price_tier)}`}>
                          {item.price_tier}
                        </Badge>
                      )}
                    </div>

                    <div className="p-5">
                      <div className="flex items-start justify-between mb-1">
                        <div>
                          <h3 className="font-bold text-gray-900 group-hover:text-gray-900 transition-colors">{item.name}</h3>
                          <p className="text-sm text-gray-500">{item.brand}</p>
                        </div>
                        <div className="text-right flex-shrink-0 ml-3">
                          {item.rating != null && (
                            <div className="flex items-center gap-1 text-sm">
                              <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                              <span className="font-medium">{item.rating}</span>
                            </div>
                          )}
                          <p className="text-sm font-semibold text-gray-900">${item.price}</p>
                        </div>
                      </div>

                      {item.bens_take && (
                        <p className="text-sm text-gray-600 italic line-clamp-2 mt-2 mb-3">
                          "{item.bens_take}"
                        </p>
                      )}

                      <div className="flex flex-wrap gap-1.5">
                        {(item.tags || []).slice(0, 3).map(tag => (
                          <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                            {tag}
                          </span>
                        ))}
                        {(item.tags || []).length > 3 && (
                          <span className="text-xs text-gray-400">+{item.tags.length - 3}</span>
                        )}
                      </div>

                      <div className="mt-4 flex items-center justify-between">
                        <span className="text-xs text-amber-600 font-medium group-hover:underline flex items-center gap-1">
                          Full Review <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>

      <FloatingActions />
      <Footer />
    </div>
  );
};

export default GearListPage;
